import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FiChevronDown } from 'react-icons/fi';

// Typecast icon component for TypeScript compatibility
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface DeliveryReportProps {
  deliveryData: {
    id: number;
    recipient: string;
    email: string;
    sent: string;
    status: string;
  }[];
}

// Utility function to determine status badge color based on delivery status
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Delivered':
      return 'bg-green-100 text-green-600';
    case 'Not Delivered':
      return 'bg-red-100 text-red-600';
    case 'Bounced':
      return 'bg-yellow-100 text-yellow-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// Renders a table displaying delivery report data for a campaign
// Supports pagination, selection, and exporting selected rows as CSV or PDF
const DeliveryReport: React.FC<DeliveryReportProps> = ({ deliveryData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<number[]>([]);
  const [exportOpen, setExportOpen] = useState(false);

  const itemsPerPage = 6;

  // Paginates the delivery data
  const totalPages = Math.ceil(deliveryData.length / itemsPerPage);
  const paginatedData = deliveryData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Toggles select-all checkbox for the current page
  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(paginatedData.map((d) => d.id));
    } else {
      setSelected([]);
    }
  };

  // Toggles selection of an individual row
  const toggleSelect = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
    );
  };

  // Exports selected rows as CSV
  const exportCSV = () => {
    const rows = deliveryData.filter((d) => selected.includes(d.id));
    const header = ['Recipient', 'Email', 'Sent At', 'Status'];
    const body = rows
      .map((r) => [r.recipient, r.email, r.sent, r.status].join(','))
      .join('\n');
    const csvContent = [header.join(','), body].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'delivery-report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Exports selected rows as PDF
  const exportPDF = () => {
    const rows = deliveryData.filter((d) => selected.includes(d.id));
    const doc = new jsPDF();
    autoTable(doc, {
      head: [['Recipient', 'Email', 'Sent At', 'Status']],
      body: rows.map((r) => [r.recipient, r.email, r.sent, r.status]),
    });
    doc.save('delivery-report.pdf');
  };

  return (
    <div className='bg-white rounded-md '>
      {/* Header with Export */}
      <div className='flex justify-between items-center px-6 py-4'>
        <h2 className='text-lg font-medium text-gray-700'>Delivery Report</h2>
        <div className='relative'>
          <button
            onClick={() => setExportOpen((prev) => !prev)}
            disabled={selected.length === 0}
            className={`px-4 py-2 rounded-sm text-sm font-medium flex items-center text-white ${
              selected.length === 0
                ? 'bg-gray-200 cursor-not-allowed'
                : 'bg-orange-500'
            }`}
          >
            Export <FiChevronDownComponent className='ml-2' />
          </button>
          {exportOpen && selected.length > 0 && (
            <div className='absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg z-10'>
              <button
                onClick={() => {
                  exportPDF();
                  setExportOpen(false);
                }}
                className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
              >
                Export as PDF
              </button>
              <button
                onClick={() => {
                  exportCSV();
                  setExportOpen(false);
                }}
                className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
              >
                Export as CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className='overflow-x-auto rounded-md mx-6 border border-gray-200 shadow-sm'>
        <table className='w-full text-left border border-gray-200'>
          <thead className='bg-gray-100'>
            <tr className='text-gray-600 text-sm border-b'>
              <th className='px-6 py-3'>
                <input
                  type='checkbox'
                  onChange={toggleSelectAll}
                  checked={
                    selected.length === paginatedData.length &&
                    paginatedData.length > 0
                  }
                  className='w-4 h-4'
                />
              </th>
              <th className='px-6 py-3 text-[#657584] text-[12px] font-normal'>
                RECIPIENT
              </th>
              <th className='px-6 py-3 text-[#657584] text-[12px] font-normal'>
                EMAIL
              </th>
              <th className='px-6 py-3 text-[#657584] text-[12px] font-normal'>
                SENT AT
              </th>
              <th className='px-6 py-3 text-[#657584] text-[12px] font-normal'>
                STATUS
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((d) => (
              <tr
                key={d.id}
                className='border-b hover:bg-gray-50 text-[#3F5265] text-[14px]'
              >
                <td className='px-6 py-4'>
                  <input
                    type='checkbox'
                    checked={selected.includes(d.id)}
                    onChange={() => toggleSelect(d.id)}
                    className='w-4 h-4'
                  />
                </td>
                <td className='px-6 py-4'>{d.recipient}</td>
                <td className='px-6 py-4'>{d.email}</td>
                <td className='px-6 py-4'>{d.sent}</td>
                <td className='px-6 py-4'>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      d.status
                    )}`}
                  >
                    {d.status}
                  </span>
                </td>
              </tr>
            ))}
            {paginatedData.length === 0 && (
              <tr>
                <td colSpan={5} className='px-6 py-6 text-center text-gray-500'>
                  No delivery data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500'>
        <span>
          Showing {paginatedData.length} results out of {deliveryData.length}
        </span>
        {totalPages > 1 && (
          <div className='flex items-center gap-2'>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className={`px-3 py-1 border rounded ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                  : 'bg-orange-500 text-white'
              }`}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentPage === i + 1
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className={`px-3 py-1 border rounded ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                  : 'bg-orange-500 text-white'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryReport;
